import type { KpiMetric } from "@codway-dev/shared";
import type { CSSProperties } from "react";
import { useId, useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ChartSeriesPoint } from "../../services/analytics.transforms";
import {
  buildEvenXAxisTicks,
  buildIntegerYAxisTicksForDomain,
} from "../../utils/chart.utils";
import {
  formatAxisDateIso,
  formatChartNumber,
  formatKpiHeadline,
  formatYAxisTick,
  type MetricFormatType,
} from "../../utils/formatters";

export type { MetricFormatType };

export type ChartTone = "blue" | "orange";

/** Pie del gráfico (UTC vs zona Shopify); texto por defecto en español. */
export const METRIC_CHART_FOOTER_DEFAULT =
  "Las fechas de este gráfico usan la zona horaria UTC, no la de tu tienda Shopify. ";

/** Azul tipo enlaces / analytics Shopify (aprox.). */
const CHART_BLUE = "#005BD3";
/** Naranja suave para serie de conversión (referencia de diseño). */
const CHART_ORANGE = "#E58845";

const GRID_LINE = "#E1E3E5";

/** Below this count of non-null points, show dots so isolated days remain visible. */
const SPARSE_SERIES_DOT_THRESHOLD = 3;

const DEFAULT_X_AXIS_TICKS = 3;

export type MetricAreaCardProps = {
  title: string;
  summaryData: KpiMetric;
  chartData: ChartSeriesPoint[];
  formatType: MetricFormatType;
  /** Serie azul (métricas habituales) u naranja (estilo conversión). */
  chartTone?: ChartTone;
  currencyCode?: string;
  /** Alto del área del gráfico (layout detalle). */
  chartHeight?: number;
  /** Máximo de etiquetas ISO en el eje X (menos en columnas estrechas). */
  maxXAxisTicks?: number;
  emphasizeValue?: boolean;
  /** Si es true, muestra la variación vs periodo anterior bajo el KPI. */
  showPeriodDelta?: boolean;
  showInfoIcon?: boolean;
  /** Texto explicativo mostrado en el tooltip de Polaris al pasar sobre el ícono de info. */
  tooltipContent?: string;
  chartFooterNote?: string;
  numberLocale?: string;
};

const P_TEXT = "var(--p-color-text, #202223)";
const P_TEXT_SECONDARY = "var(--p-color-text-secondary, #6D7175)";
const P_BORDER = "var(--p-color-border-secondary, #E1E3E5)";

const TOOLTIP_BOX_STYLE: CSSProperties = {
  background: "var(--p-color-bg-surface, #fff)",
  border: `1px solid ${P_BORDER}`,
  borderRadius: 8,
  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  padding: "8px 10px",
};

function formatTooltipMetricValue(
  value: unknown,
  formatType: MetricFormatType,
  currencyCode: string,
  numberLocale: string
): string {
  if (value == null) return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "—";
  return formatChartNumber(formatType, num, currencyCode, numberLocale);
}

type MetricChartTooltipContentProps = {
  active?: boolean;
  payload?: ReadonlyArray<{ value?: unknown }>;
  label?: string | number;
  formatType: MetricFormatType;
  currencyCode: string;
  numberLocale: string;
};

function MetricChartTooltipContent({
  active,
  payload,
  label,
  formatType,
  currencyCode,
  numberLocale,
}: MetricChartTooltipContentProps) {
  if (!active || !payload?.length) return null;

  return (
    <div style={TOOLTIP_BOX_STYLE}>
      <div
        style={{
          color: P_TEXT_SECONDARY,
          fontSize: 12,
          marginBottom: 4,
        }}
      >
        {formatAxisDateIso(String(label ?? ""))}
      </div>
      <div style={{ color: P_TEXT, fontSize: 12, fontWeight: 600 }}>
        {formatTooltipMetricValue(
          payload[0]?.value,
          formatType,
          currencyCode,
          numberLocale
        )}
      </div>
    </div>
  );
}

type SparseSeriesDotProps = {
  cx?: number;
  cy?: number;
  payload?: ChartSeriesPoint;
  fill: string;
};

function SparseSeriesDot({ cx, cy, payload, fill }: SparseSeriesDotProps) {
  const value = payload?.value;
  if (value == null || cx == null || cy == null) {
    return null;
  }
  return <circle cx={cx} cy={cy} r={3} fill={fill} strokeWidth={0} />;
}

function colorPorTono(tone: ChartTone): string {
  return tone === "orange" ? CHART_ORANGE : CHART_BLUE;
}

export function MetricAreaCard({
  title,
  summaryData,
  chartData,
  formatType,
  chartTone = "blue",
  currencyCode = "USD",
  chartHeight = 236,
  maxXAxisTicks = DEFAULT_X_AXIS_TICKS,
  emphasizeValue = true,
  showPeriodDelta = false,
  showInfoIcon = true,
  tooltipContent,
  chartFooterNote = METRIC_CHART_FOOTER_DEFAULT,
  numberLocale = "en-US",
}: MetricAreaCardProps) {
  const reactId = useId().replace(/[^a-zA-Z0-9_-]/g, "") || "chart";
  const gradientId = `${reactId}-fill`;
  const tooltipId = `${reactId}-tooltip`;
  const hasTooltip = showInfoIcon && Boolean(tooltipContent);

  const accent = colorPorTono(chartTone);

  const yAxisConfig = useMemo(() => {
    const values = chartData
      .map((point) => point.value)
      .filter((v): v is number => v != null && Number.isFinite(v));
    const seriesMax = values.length > 0 ? Math.max(...values) : 0;

    if (formatType === "integer") {
      const domainMax = Math.max(Math.ceil(seriesMax), 1);
      return {
        allowDecimals: false as const,
        ticks: buildIntegerYAxisTicksForDomain(domainMax),
        domain: [0, domainMax] as [number, number],
      };
    }

    if (formatType === "percent" || formatType === "percent_whole") {
      return {
        allowDecimals: false as const,
        ticks: [0, 25, 50, 75, 100],
        domain: [0, 100] as [number, number],
      };
    }

    return {
      allowDecimals: true as const,
      ticks: undefined,
      domain: undefined,
    };
  }, [chartData, formatType]);

  const validPointCount = useMemo(
    () => chartData.filter((point) => point.value != null).length,
    [chartData]
  );

  const showSparseDots =
    formatType === "percent" ||
    formatType === "percent_whole" ||
    validPointCount <= SPARSE_SERIES_DOT_THRESHOLD;

  const xAxisTicks = useMemo(
    () =>
      buildEvenXAxisTicks(
        chartData.map((point) => point.date),
        maxXAxisTicks
      ),
    [chartData, maxXAxisTicks]
  );

  const deltaLong =
    summaryData.change_pct === null
      ? "Sin comparación con el periodo anterior (valor previo en 0)"
      : `${summaryData.change_pct >= 0 ? "+" : ""}${summaryData.change_pct.toFixed(1)}% frente al periodo anterior`;

  const headline = formatKpiHeadline(
    formatType,
    summaryData.value,
    currencyCode,
    numberLocale
  );

  const valueFontSize = emphasizeValue ? "1.625rem" : "1.35rem";

  return (
    <s-box
      background="base"
      border="small subdued solid"
      borderRadius="large-100"
      padding="large-200"
    >
      <s-stack direction="block" gap="small-200">
        <div
          style={{
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: 4,
          }}
        >
          <s-text type="strong">{title}</s-text>
          {hasTooltip ? (
            <span style={{ display: "inline-flex", flexShrink: 0 }}>
              <s-clickable
                interestFor={tooltipId}
                aria-label={`Más información sobre ${title}`}
              >
                <span
                  style={{ display: "inline-flex", color: P_TEXT_SECONDARY }}
                >
                  <s-icon type="info" />
                </span>
              </s-clickable>
            </span>
          ) : showInfoIcon ? (
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                flexShrink: 0,
                color: P_TEXT_SECONDARY,
              }}
            >
              <s-icon type="info" />
            </span>
          ) : null}
        </div>
        {hasTooltip ? (
          <s-tooltip id={tooltipId}>{tooltipContent}</s-tooltip>
        ) : null}

        <div
          style={{
            marginTop: 2,
            fontSize: valueFontSize,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "-0.02em",
            color: P_TEXT,
          }}
        >
          {headline}
        </div>

        {showPeriodDelta ? (
          summaryData.change_pct === null ? (
            <s-text color="subdued">{deltaLong}</s-text>
          ) : (
            <s-text tone={summaryData.is_positive ? "success" : "critical"}>
              {deltaLong}
            </s-text>
          )
        ) : null}

        <div style={{ width: "100%", height: chartHeight, marginTop: 10 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: -5, bottom: 0 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke={GRID_LINE}
                strokeDasharray="4 4"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                ticks={xAxisTicks}
                interval={0}
                padding={{ left: 4, right: 20 }}
                height={36}
                tick={{ fill: P_TEXT_SECONDARY, fontSize: 10 }}
                tickFormatter={formatAxisDateIso}
                tickLine={false}
                axisLine={{ stroke: P_BORDER }}
              />
              <YAxis
                tick={{ fill: P_TEXT_SECONDARY, fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={yAxisConfig.allowDecimals}
                domain={yAxisConfig.domain}
                ticks={yAxisConfig.ticks}
                tickFormatter={(v: number) =>
                  formatYAxisTick(formatType, v, currencyCode, numberLocale)
                }
                width={48}
              />
              <Tooltip
                cursor={{ stroke: P_BORDER, strokeWidth: 1 }}
                content={(tooltipProps) => (
                  <MetricChartTooltipContent
                    {...tooltipProps}
                    formatType={formatType}
                    currencyCode={currencyCode}
                    numberLocale={numberLocale}
                  />
                )}
              />
              <Area
                type="monotone"
                dataKey="value"
                name={title}
                stroke={accent}
                strokeWidth={1.35}
                fill={`url(#${gradientId})`}
                fillOpacity={1}
                connectNulls
                dot={
                  showSparseDots
                    ? (dotProps) => (
                        <SparseSeriesDot
                          cx={dotProps.cx}
                          cy={dotProps.cy}
                          payload={
                            dotProps.payload as ChartSeriesPoint | undefined
                          }
                          fill={accent}
                        />
                      )
                    : false
                }
                activeDot={{ r: 3, fill: accent, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div
          style={{
            marginTop: 10,
            paddingInline: 8,
            textAlign: "center",
          }}
        >
          <span
            style={{
              fontSize: 12,
              lineHeight: 1.45,
              color: P_TEXT_SECONDARY,
            }}
          >
            {chartFooterNote}
          </span>
        </div>
      </s-stack>
    </s-box>
  );
}
